import type { CustomField, CustomOption } from '@interfaces/customField';
import type { TranslatedString } from '@interfaces/shared';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { useChartColorBySupport } from '@ovComponents/2-component/pieChart/getChartColors';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { isHouseholdModelPortfolioAssetClassStrategy } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type { HouseholdModelPortfolioModel } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { formatMoneyValue, formatPercentPoints } from '@utils/moneyFormatting';
import { Alert, Box, Grid, Typography, useTheme } from 'hephaestus/web';
import { round } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  type HoldingsProblem,
  holdingsProblem,
} from '../householdModelPortfolioDraft/householdAllocations';
import { buildEnrichedCustomOptions } from '../utils';
import {
  type AllocationComparisonRow,
  type AllocationPercentageView,
  type AssetClassBranchComparisonRow,
  allocationChange,
  allocationComparisonRows,
  allocationDisplayDelta,
  allocationDisplayWeight,
  allocationSideValueCents,
  allocationTotal,
  allocationTotalValueCents,
  assetClassBranchComparisonRows,
  changedAllocationCountForView,
  isAllocationChangedForView,
} from './allocationComparison';

const TEST_ID = 'review-allocations';

export type ReviewAllocationsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  displayCurrentAllocations?: boolean;
  approveButton?: TranslatedString;
} & Record<string, any>;

export type ReviewAllocationsVisualProps = {
  options: ReviewAllocationsOptions;
  proposed: HouseholdModelPortfolioModel;
  /** Absent when the workflow never learnt what the household had before. */
  current?: HouseholdModelPortfolioModel;
  /**
   * What the household holds today, which the target weights are read against.
   * Absent when the balance could not be read, in which case the table stays on
   * weights alone.
   */
  householdMarketValueCents?: number;
  /** Currency symbol for the household balance, e.g. `$` or `€`. */
  currency?: string;
  loading?: boolean;
  onApprove: () => void | Promise<void>;
  /** Household custom-field values this step is collecting. */
  householdFieldData?: { customFields: { key: string; value?: unknown }[] };
  updateHouseholdFieldData?: (value: {
    customFields: { key: string; value?: unknown }[];
  }) => void;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
};

const weight = (value?: number) =>
  value == null ? '—' : formatPercentPoints(value);

const AllocationWeightDisplay = ({
  row,
  side,
  view,
}: {
  row: AllocationComparisonRow;
  side: 'current' | 'proposed';
  view: AllocationPercentageView;
}) => <>{weight(allocationDisplayWeight(row, side, view))}</>;

const AllocationChangeDisplay = ({
  row,
  view,
}: {
  row: AllocationComparisonRow;
  view: AllocationPercentageView;
}) => <AllocationDelta delta={allocationDisplayDelta(row, view)} />;

/**
 * Always to the cent, whole amount or not: a money column is read down, and
 * figures that drop their decimals break the alignment the reader scans by.
 */
const money = (cents?: number, currency = '$') =>
  cents == null ? '—' : formatMoneyValue(cents, currency, 2, false);

/**
 * Why the change cannot be sent, in the words of the thing the advisor would
 * have to go back and fix.
 */
const BLOCKED_COPY: Record<HoldingsProblem | 'MISSING_CURRENT', string> = {
  EMPTY: 'nothingProposed',
  DUPLICATE: 'duplicateHolding',
  WEIGHTS: 'doesNotAddUp',
  GROUP_HOLDINGS: 'groupsDoNotAddUp',
  GROUP_WEIGHTS: 'groupWeightsDoNotAddUp',
  MISSING_CURRENT: 'missingCurrent',
};

/**
 * The holdings being proposed, against the ones the household has today.
 *
 * Continuing from here sends the proposal for signature, so this is the last
 * place the change can be read whole — which is why a holding being taken away
 * is shown as plainly as one being added, and why weights that no longer
 * describe a whole model stop the step rather than the service.
 */
export const ReviewAllocationsVisual = ({
  options,
  proposed,
  current,
  householdMarketValueCents,
  currency = '$',
  loading = false,
  onApprove,
  householdFieldData = { customFields: [] },
  updateHouseholdFieldData,
  activeCustomFields,
  derivedCustomFields,
}: ReviewAllocationsVisualProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const [focused, setFocused] = useState<string[]>([]);
  const [customOptionSelected, setCustomOptionSelected] = useState<
    CustomOption[]
  >([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: householdFieldData.customFields,
    });
  const showsCurrent =
    options?.displayCurrentAllocations !== false && !!current;

  const rows = useMemo(
    () =>
      allocationComparisonRows(
        translateBackendString,
        proposed,
        showsCurrent ? current : undefined,
      ),
    [proposed, current, showsCurrent],
  );
  const grouped = rows.some((row) => !!row.assetClassName);
  /** Neither model has a holding to its name, so there is no table to draw. */
  const nothingToRead = rows.length === 0;
  // A proposal is signed against what the household holds today, and
  // product-service will not take one that cannot say what that was. The
  // advisor can do nothing about it here, so it is said before they read the
  // change rather than after they approve it.
  const blocked =
    holdingsProblem(proposed) ?? (current ? undefined : 'MISSING_CURRENT');
  // An asset class model's weights are read within their group, so a figure
  // totalling them across the model would be a hundred per group and mean
  // nothing. The check above still speaks for whether they are whole.
  const showsTotal = !isHouseholdModelPortfolioAssetClassStrategy(proposed);
  const showsPortfolioWidePercentages =
    isHouseholdModelPortfolioAssetClassStrategy(proposed);

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  return (
    <WorkflowCompletionForm
      onSubmit={() => {
        if (validateAllCustomFields().length > 0) return;
        void onApprove();
      }}
      testId={`${TEST_ID}-form`}
      submitButtonProps={{
        label: translateBackendString(options?.approveButton) || t('continue'),
        disabled: loading || !!blocked,
        dataTestId: `${TEST_ID}-approve-button`,
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid size={12}>
          {nothingToRead ? (
            <Typography
              variant='bodyMedium'
              sx={{ fontWeight: 500 }}
              data-testid={`${TEST_ID}-empty`}
            >
              {t('householdModelPortfolioAllocations.empty')}
            </Typography>
          ) : (
            <AllocationTable
              proposed={proposed}
              current={showsCurrent ? current : undefined}
              rows={rows}
              showsCurrent={showsCurrent}
              householdMarketValueCents={householdMarketValueCents}
              currency={currency}
              grouped={grouped}
              showsTotal={showsTotal}
              showsPortfolioWidePercentages={showsPortfolioWidePercentages}
            />
          )}
        </Grid>

        {/* A proposal emptied of everything the household held still has rows to
            read — one per holding being taken away — so the table is no
            explanation for a button that will not press. */}
        {blocked && !(blocked === 'EMPTY' && nothingToRead) && (
          <Grid size={12}>
            <Alert severity='warning'>
              <Typography
                variant='bodyMedium'
                sx={{ fontWeight: 500 }}
                data-testid={`${TEST_ID}-blocked`}
              >
                {t(
                  `householdModelPortfolioAllocations.${BLOCKED_COPY[blocked]}`,
                )}
              </Typography>
            </Alert>
          </Grid>
        )}

        {customOptionSelected.length > 0 && updateHouseholdFieldData && (
          <Grid size={12}>
            {renderCustomFields({
              customData: householdFieldData,
              update: updateHouseholdFieldData,
              focused,
              setFocused,
              loading,
            })}
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

const AssetClassColorDot = ({ color }: { color: string | undefined }) => (
  <Box
    aria-hidden
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color ?? 'grey.400',
      flexShrink: 0,
    }}
  />
);

const AssetClassBranchSummaryTable = ({
  rows,
  branchColorByClassId,
  label,
  sectionLabel,
}: {
  rows: AssetClassBranchComparisonRow[];
  branchColorByClassId: Map<string, string | undefined>;
  label: (key: string) => string;
  sectionLabel: string;
}) => (
  <Box
    data-testid={`${TEST_ID}-asset-class-branches-section`}
    sx={{
      maxWidth: { xs: '100%', md: '50%' },
      mb: 2.5,
    }}
  >
    <Typography
      variant='labelSmall'
      component='p'
      sx={{ color: 'text.secondary', mb: 1 }}
    >
      {sectionLabel}
    </Typography>
    <Table
      data-testid={`${TEST_ID}-asset-class-branches-table`}
      sx={{ tableLayout: 'fixed', width: '100%' }}
    >
      <TableHead>
        <TableRow>
          <TableCell isFirst bold dense>
            {label('assetClass')}
          </TableCell>
          <TableCell bold number dense maxWidth='88px'>
            {label('portfolioPercentSubheader')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.assetClassId}
            data-testid={`${TEST_ID}-asset-class-branch-row-${row.assetClassId}`}
          >
            <TableCell isFirst dense>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <AssetClassColorDot
                  color={branchColorByClassId.get(row.assetClassId)}
                />
                {row.name}
              </Box>
            </TableCell>
            <TableCell
              number
              dense
              data-testid={`${TEST_ID}-asset-class-branch-percent-${row.assetClassId}`}
            >
              {weight(row.proposed ?? row.current ?? undefined)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

/**
 * A holding whose weight the change leaves alone is only there to be read past,
 * so the ones that move are the ones the table spends its emphasis on: a tinted
 * row, a signed difference, and — where the holding itself comes or goes — a
 * badge, none of which is carried by colour alone.
 */
const AllocationTable = ({
  proposed,
  current,
  rows,
  showsCurrent,
  householdMarketValueCents,
  currency,
  grouped,
  showsTotal,
  showsPortfolioWidePercentages,
}: {
  proposed: HouseholdModelPortfolioModel;
  current?: HouseholdModelPortfolioModel;
  rows: AllocationComparisonRow[];
  showsCurrent: boolean;
  householdMarketValueCents?: number;
  currency: string;
  grouped: boolean;
  showsTotal: boolean;
  showsPortfolioWidePercentages: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions']);
  const theme = useTheme();
  const chartColors = useChartColorBySupport();
  const colorKeys = useMemo(() => Object.keys(chartColors), [chartColors]);
  const [onlyChanges, setOnlyChanges] = useState(false);
  const [percentageView, setPercentageView] =
    useState<AllocationPercentageView>('portfolio');
  const label = (key: string) => t(`householdModelPortfolioAllocations.${key}`);
  const portfolioAllocationSummary = t(
    'householdModelPortfolioAllocations.portfolioAllocationSummary',
  );
  const activePercentageView: AllocationPercentageView =
    showsPortfolioWidePercentages ? percentageView : 'assetClass';

  const assetClassBranchRows = useMemo(
    () =>
      assetClassBranchComparisonRows(translateBackendString, proposed, current),
    [translateBackendString, proposed, current],
  );
  const branchColorByClassId = useMemo(() => {
    const map = new Map<string, string | undefined>();
    (proposed.assetClassBranches ?? []).forEach((branch, index) => {
      const assetClassId = branch.assetClass?.id;
      if (!assetClassId) return;
      const colorKey =
        branch.color && colorKeys.includes(branch.color)
          ? branch.color
          : colorKeys[index % colorKeys.length];
      map.set(assetClassId, chartColors[colorKey as keyof typeof chartColors]);
    });
    return map;
  }, [proposed.assetClassBranches, chartColors, colorKeys]);

  // Without today's weights beside them there is no change to read, only a
  // list of what is being proposed.
  const changed = showsCurrent
    ? changedAllocationCountForView(rows, activePercentageView)
    : 0;
  const readable =
    onlyChanges && showsCurrent
      ? rows.filter((row) =>
          isAllocationChangedForView(row, activePercentageView),
        )
      : rows;
  const hasHouseholdBalance =
    householdMarketValueCents != null && householdMarketValueCents > 0;
  const showsCurrentValues = hasHouseholdBalance && showsCurrent;
  const showsProposedValues = hasHouseholdBalance;
  // Shared across the full model (not the filtered rows) so a holding hidden by
  // "only show changes" still keeps its share of the cents that add up under
  // the column.
  const currentValueById = useMemo(() => {
    const values = allocationSideValueCents(
      rows,
      'current',
      householdMarketValueCents,
    );
    return new Map(
      rows.map((row, index) => [row.financialProductId, values[index]]),
    );
  }, [rows, householdMarketValueCents]);
  const proposedValueById = useMemo(() => {
    const values = allocationSideValueCents(
      rows,
      'proposed',
      householdMarketValueCents,
    );
    return new Map(
      rows.map((row, index) => [row.financialProductId, values[index]]),
    );
  }, [rows, householdMarketValueCents]);
  const valueOf = (cents?: number) => money(cents, currency);

  const showsMoneyColumns = showsCurrentValues || showsProposedValues;

  const percentHeader = (side: 'current' | 'proposed') =>
    label(showsMoneyColumns ? `${side}Percent` : side);

  return (
    <>
      {showsPortfolioWidePercentages && assetClassBranchRows.length > 0 && (
        <AssetClassBranchSummaryTable
          rows={assetClassBranchRows}
          branchColorByClassId={branchColorByClassId}
          label={label}
          sectionLabel={portfolioAllocationSummary}
        />
      )}

      {(showsCurrent || showsPortfolioWidePercentages) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
          }}
        >
          {showsCurrent && (
            <Typography
              variant='bodySmall'
              sx={{ color: theme.color.onSurfaceVariant }}
              data-testid={`${TEST_ID}-change-summary`}
            >
              {changed === 0
                ? label('noChanges')
                : t('householdModelPortfolioAllocations.changeCount', {
                    count: changed,
                    total: rows.length,
                  })}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              ml: showsCurrent ? 'auto' : undefined,
            }}
          >
            {showsPortfolioWidePercentages && (
              <SwitchField
                size='small'
                label={label('percentageViewAssetClass')}
                checked={percentageView === 'assetClass'}
                onChange={(checked) =>
                  setPercentageView(checked ? 'assetClass' : 'portfolio')
                }
                data-testid={`${TEST_ID}-percentage-view`}
              />
            )}
            {showsCurrent && (
              <SwitchField
                size='small'
                label={label('onlyChanges')}
                checked={onlyChanges}
                onChange={setOnlyChanges}
                data-testid={`${TEST_ID}-only-changes`}
              />
            )}
          </Box>
        </Box>
      )}

      {/* The balance every amount on the table is a share of. It belongs to the
          household rather than to any one column, so it is said here instead of
          as a row among the holdings. */}
      {(showsCurrentValues || showsProposedValues) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography
            variant='bodyMedium'
            sx={{ color: theme.color.positive, fontWeight: 'bold' }}
          >
            {label('currentBalance')}
          </Typography>
          <Typography
            variant='bodyMedium'
            sx={{ color: theme.color.positive, fontWeight: 'bold' }}
            data-testid={`${TEST_ID}-balance`}
          >
            {money(householdMarketValueCents, currency)}
          </Typography>
        </Box>
      )}

      <Table data-testid={`${TEST_ID}-table`}>
        <TableHead>
          <TableRow>
            <TableCell isFirst bold>
              {label('security')}
            </TableCell>
            {grouped && <TableCell bold>{label('assetClass')}</TableCell>}
            {/* Like beside like. The two weights are what the reader compares,
                and the two amounts likewise; a side's weight and amount are the
                same fact told twice, so putting those two together would only
                hold the comparison apart. A weight column sitting beside a
                money column has to say which one it is; on its own it does
                not. */}
            {showsCurrent && (
              <TableCell bold number>
                {percentHeader('current')}
              </TableCell>
            )}
            <TableCell bold number>
              {percentHeader('proposed')}
            </TableCell>
            {showsCurrentValues && (
              <TableCell bold number>
                {label('currentValue')}
              </TableCell>
            )}
            {showsProposedValues && (
              <TableCell bold number>
                {label('proposedValue')}
              </TableCell>
            )}
            {showsCurrent && (
              <TableCell bold number>
                {label('change')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {readable.length === 0 && rows.length > 0 && (
            <TableRow data-testid={`${TEST_ID}-no-changes-row`}>
              <TableCell
                isFirst
                colSpan={
                  2 +
                  Number(grouped) +
                  Number(showsCurrent) * 2 +
                  Number(showsCurrentValues) +
                  Number(showsProposedValues)
                }
                sx={{ color: theme.color.onSurfaceVariant }}
              >
                {label('noChangesInView')}
              </TableCell>
            </TableRow>
          )}
          {readable.map((row) => {
            const changeKind = showsCurrent
              ? allocationChange(row)
              : 'UNCHANGED';
            const moved =
              changeKind === 'ADDED' ||
              changeKind === 'REMOVED' ||
              isAllocationChangedForView(row, activePercentageView);

            return (
              <TableRow
                key={row.financialProductId}
                data-testid={`${TEST_ID}-row-${row.financialProductId}`}
                backgroundColor={
                  moved ? theme.color.surfaceContainer : undefined
                }
              >
                <TableCell isFirst>
                  {row.name}
                  {row.ticker && (
                    <Box component='span' sx={{ ml: 1, opacity: 0.6 }}>
                      {row.ticker}
                    </Box>
                  )}
                  {(changeKind === 'ADDED' || changeKind === 'REMOVED') && (
                    <Box component='span' sx={{ ml: 1 }}>
                      <Badge
                        label={label(
                          changeKind === 'ADDED'
                            ? 'newHolding'
                            : 'removedHolding',
                        )}
                        color={changeKind === 'ADDED' ? 'positive' : 'negative'}
                        testId={`${TEST_ID}-badge-${row.financialProductId}`}
                      />
                    </Box>
                  )}
                </TableCell>
                {grouped && <TableCell>{row.assetClassName ?? '—'}</TableCell>}
                {showsCurrent && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-current-${row.financialProductId}`}
                  >
                    <AllocationWeightDisplay
                      row={row}
                      side='current'
                      view={activePercentageView}
                    />
                  </TableCell>
                )}
                <TableCell
                  number
                  bold={moved}
                  data-testid={`${TEST_ID}-proposed-${row.financialProductId}`}
                >
                  <AllocationWeightDisplay
                    row={row}
                    side='proposed'
                    view={activePercentageView}
                  />
                </TableCell>
                {showsCurrentValues && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-current-value-${row.financialProductId}`}
                  >
                    {valueOf(currentValueById.get(row.financialProductId))}
                  </TableCell>
                )}
                {showsProposedValues && (
                  <TableCell
                    number
                    bold={moved}
                    data-testid={`${TEST_ID}-proposed-value-${row.financialProductId}`}
                  >
                    {valueOf(proposedValueById.get(row.financialProductId))}
                  </TableCell>
                )}
                {showsCurrent && (
                  <TableCell
                    number
                    data-testid={`${TEST_ID}-change-${row.financialProductId}`}
                  >
                    <AllocationChangeDisplay
                      row={row}
                      view={activePercentageView}
                    />
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {/* Totalled over every holding, not only the ones on screen: what the
              filter leaves out is still part of the model being proposed. */}
          {showsTotal && (
            <TableRow data-testid={`${TEST_ID}-total`}>
              <TableCell isFirst bold>
                {label('total')}
              </TableCell>
              {grouped && <TableCell />}
              {showsCurrent && (
                <TableCell bold number data-testid={`${TEST_ID}-current-total`}>
                  {weight(allocationTotal(rows, 'current'))}
                </TableCell>
              )}
              <TableCell bold number data-testid={`${TEST_ID}-proposed-total`}>
                {weight(allocationTotal(rows, 'proposed'))}
              </TableCell>
              {showsCurrentValues && (
                <TableCell
                  bold
                  number
                  data-testid={`${TEST_ID}-current-value-total`}
                >
                  {money(
                    allocationTotalValueCents(
                      rows,
                      'current',
                      householdMarketValueCents,
                    ),
                    currency,
                  )}
                </TableCell>
              )}
              {showsProposedValues && (
                <TableCell
                  bold
                  number
                  data-testid={`${TEST_ID}-proposed-value-total`}
                >
                  {money(
                    allocationTotalValueCents(
                      rows,
                      'proposed',
                      householdMarketValueCents,
                    ),
                    currency,
                  )}
                </TableCell>
              )}
              {showsCurrent && (
                <TableCell bold number data-testid={`${TEST_ID}-change-total`}>
                  <AllocationDelta
                    delta={round(
                      allocationTotal(rows, 'proposed') -
                        allocationTotal(rows, 'current'),
                      2,
                    )}
                  />
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};

/**
 * The difference between the two weights on a row, signed and pointed, so the
 * direction survives both a greyscale screen and a reader that only has the
 * text.
 */
const AllocationDelta = ({
  delta,
  muted = false,
  showArrow = true,
  compact = false,
}: {
  delta: number;
  muted?: boolean;
  showArrow?: boolean;
  compact?: boolean;
}) => {
  const theme = useTheme();

  if (delta === 0) {
    return (
      <Box
        component='span'
        sx={{
          color: theme.color.onSurfaceVariant,
          fontWeight: 'regular',
        }}
      >
        —
      </Box>
    );
  }

  const up = delta > 0;
  const Arrow = up ? ArrowUpwardIcon : ArrowDownwardIcon;

  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        color: muted
          ? theme.color.onSurfaceVariant
          : up
            ? theme.color.positive
            : theme.color.negative,
        fontWeight: compact || muted ? 'regular' : 'bold',
      }}
    >
      {showArrow && (
        <Arrow sx={{ fontSize: compact ? '1em' : '14px' }} aria-hidden />
      )}
      {`${up ? '+' : '−'}${round(Math.abs(delta), 2)}%`}
    </Box>
  );
};

export default ReviewAllocationsVisual;
